import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';

import dayjs from 'dayjs';
import cn from 'classnames';

import { GetContractDetails } from '@/graphql/contracts/_generated_/getContractDetails.query';
import Dropdown from '@/components/common/Dropdown/Dropdown';
import { getdropDownCommonStyling } from '@/components/common/Dropdown/dropDownTag4Styling';

import styles from './uplift-headers.module.scss';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';

import ContractHeaderDatePickerItem from '@/components/common/ContractHeaderItem/ContractHeaderDatePickerItem';
import ContractHeaderInputItem from '@/components/common/ContractHeaderItem/ContractHeaderInputItem';
import { getUpliftTypes } from '@/utils/data-helpers/upliftTypes';

import { UpdateContractUplift } from '../hooks/useUpdateContractUpliftSettings';
import TopLabel from '@/components/common/TopLabel/TopLabel';
import Skeleton from '@/components/common/Skeleton/Skeleton';
import { FormattingUtils } from '@/utils/Formatting.utils';
import { NumberUtils } from '@/utils/numbers.utils';
import { FieldVerificationTypeEnum, UpliftType } from '@/graphql/contracts/_generated_/contracts';
import { Textarea } from '@/components/common/TextAreaTag4/TextareaTag4';
import { validationSchema } from './validation';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';
import VerificationTooltipIcon from '@/components/common/ContractHeaderItem/VerificationTooltipIcon';
import { FieldVerificationMap } from '../../ContractHeaders/hooks/useGetLatestFieldVerifications';

interface IHeadersProps {
  isLoading: boolean;
  upliftSection: GetContractDetails['contract']['contractUpliftSettings'] | undefined;
  onSubmit: (upliftData: UpdateContractUplift, cbOnError?: (() => void) | undefined) => Promise<void>
  isDisabledEditField: boolean;
  contractStartDate?: Date;
  fieldVerificationMap?: FieldVerificationMap;
  verificationUsers?: User[];
}

export default function UpliftHeaders({
  isLoading,
  upliftSection,
  onSubmit,
  isDisabledEditField,
  contractStartDate,
  fieldVerificationMap,
  verificationUsers,
}: IHeadersProps) {
  const renderVerificationIcon = (dbFieldName: string) => {
    const verification = fieldVerificationMap?.[dbFieldName];
    if (!verification || verification.type === FieldVerificationTypeEnum.UNVERIFY) return null;

    const user = verificationUsers?.find((u) => u.Id === verification.userID);
    const userName = user
      ? `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim() || 'Unknown'
      : 'Loading...';

    return (
      <VerificationTooltipIcon
        userName={userName}
        verifiedAt={verification.verifiedAt}
      />
    );
  };

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const upliftTypes = getUpliftTypes();
  const { searchUsers, isLoadingSearchUsers } = useSearchUsers();
  const [lastUpdateUser, setLastUpdateUser] = useState<User | null>(null);

  useEffect(() => {
    if (upliftSection?.updatedBy) {
      const fetchData = async () => {
        var result = await searchUsers({ ids: [upliftSection.updatedBy!] });
        setLastUpdateUser(result?.[0]);
      };

      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upliftSection?.updatedBy]);

  return (
    <>
      <Formik
        initialValues={{
          upliftTypeId: upliftSection?.upliftType,
          validThrough: upliftSection?.validThrough ?? undefined,
          notes: upliftSection?.notes ?? '',
          value: upliftSection?.value ? parseFloat(upliftSection?.value).toFixed(2) : undefined,
          contractStartDate
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={() => {
          setShowConfirmationModal(true);
        }}
      >
        {({ values, setFieldValue, setFieldTouched, resetForm, errors, touched, isValid, dirty }) => (
          <>
            <AgConfirmationModal
              isOpen={showConfirmationModal}
              onClose={() => {
                setShowConfirmationModal(false);
                resetForm();
              }}
              closeOnConfirm={false}
              onConfirm={() => {
                if (values.upliftTypeId) {
                  const upliftTypeValue = values.upliftTypeId.isValueRequired && values.value ?
                    +NumberUtils.parseNumericInput(values.value).toFixed(2) : undefined;
                  const changedValues = {
                    upliftTypeId: values.upliftTypeId?.id,
                    validThrough: values.validThrough,
                    notes: values.notes,
                    value: upliftTypeValue
                  };
                  onSubmit({ ...changedValues }, resetForm);
                  setShowConfirmationModal(false);
                }
              }}
              title={'You are about to modify the Uplift Settings for the Contract'}
            >
              You are about to modify the Uplift Settings for the Contract. <br />
              The updated settings will be visible to the users and might impact decision making on Uplifts.
              <br />
              Please confirm you wish to continue.
            </AgConfirmationModal>
            <Form>
              <div className={styles['contract-header-contrainer']}>
                <div className={styles['header-inputs']}>
                  <div className={styles['header-group']}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                      <TopLabel label='Uplift Type' id="uplift-type" withMargin={false} />
                      {renderVerificationIcon('Uplift Type')}
                    </div>
                    {isLoading ?
                      <Skeleton /> :
                      <Dropdown<UpliftType>
                        disabled={isDisabledEditField}
                        id="uplift-type"
                        optionsData={upliftTypes}
                        defaultValue={values.upliftTypeId}
                        isLoading={isLoading}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        selectedOption={values.upliftTypeId}
                        menuPortalTarget={document.querySelector('body')}
                        placeholder={<div className={styles['dropdown-placeholder']}>Select uplift type...</div>}
                        customStyles={{
                          container: (provided, { isDisabled }) => ({
                            ...provided,
                            ...getdropDownCommonStyling(isDisabled)
                          }),
                        }}
                        handleChange={(newValue) => {
                          setFieldTouched('upliftTypeId', true, false);
                          setFieldValue('upliftTypeId', newValue ?? null);
                        }}
                      />
                    }
                  </div>
                  {values.upliftTypeId?.isValueRequired && (
                    <div className={styles['header-group']}>
                      <ContractHeaderInputItem
                        isLoading={isLoading}
                        label="Capping Option Uplift (%)"
                        name="value"
                        type="number"
                        step="any"
                        value={values?.value}
                        disabled={isDisabledEditField}
                        errorMessage={touched && errors.value}
                        onChange={(e) => {
                          setFieldValue('value', e.target.value);
                          setFieldTouched('value', true, false);
                        }}
                        inputStyle={{ width: '100%', textAlign: 'right' }}
                        labelSuffix={renderVerificationIcon('Capping Option Uplift')}
                      />
                      {errors.value && values.upliftTypeId.isValueRequired && (
                        <ValidationAlert isFloating={false}>{errors.value}</ValidationAlert>
                      )}
                    </div>)}
                  <div className={styles['header-group']}>
                    <ContractHeaderDatePickerItem
                      value={values?.validThrough ?
                        new Date(values.validThrough) : undefined}
                      isLoading={isLoading}
                      name="validThrough"
                      label="Uplift Type Valid Until"
                      disabled={isDisabledEditField}
                      onChange={({ value }) => {
                        if (dayjs(value).isSame(upliftSection?.validThrough, 'day') && !errors.validThrough) {
                          setFieldValue('validThrough', upliftSection?.validThrough);
                          setFieldTouched('validThrough', false, true);
                          return;
                        }
                        setFieldTouched('validThrough', true, false);
                        setFieldValue('validThrough', value ?? null);
                      }}
                      labelSuffix={renderVerificationIcon('Uplift Type Valid Until')}
                    />
                    {errors.validThrough && (
                      <ValidationAlert isFloating={false}>{errors.validThrough}</ValidationAlert>
                    )}
                  </div>
                  <div className={styles['header-group']}>
                    <TopLabel label='Uplift Notes' id="uplift-notes" />
                    {isLoading ?
                      <Skeleton /> :
                      <>
                        <Textarea
                          className={cn(styles['text-area'], {
                            [styles.disabled]: isDisabledEditField
                          })}
                          id="uplift-notes"
                          name="notes"
                          disabled={isDisabledEditField}
                          value={values?.notes}
                          resize='none'
                          onChange={(e) => {
                            setFieldValue('notes',
                              FormattingUtils.replaceControlCharacters(e.target.value)
                                ? e.target.value
                                : '');
                            setFieldTouched('notes', true, false);
                          }}
                        />
                      </>
                    }
                  </div>
                  <div className={styles['header-group']}>
                    <ContractHeaderDatePickerItem
                      value={upliftSection?.updated ?
                        new Date(upliftSection.updated) : undefined}
                      isLoading={isLoading}
                      name="upliftSettingsUpdated"
                      label="Uplift Settings Last Updated"
                      disabled={true}
                    />
                  </div>

                  <div className={styles['header-group']}>
                    <ContractHeaderInputItem
                      isLoading={isLoading || isLoadingSearchUsers}
                      label="Settings Updated By"
                      value={lastUpdateUser ? `${lastUpdateUser.FirstName} ${lastUpdateUser.LastName}` : undefined}
                      name="upliftSettingsUpdatedBy"
                      disabled={true}
                      inputStyle={{ width: '100%' }}
                    />
                  </div>
                </div>
                <div className={styles['save-btn']}>
                  <Button
                    type="submit"
                    buttonStyleType={ButtonStyleType.PRIMARY}
                    disabled={!isValid || !dirty}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik >

    </>
  );
}
